import { MAX_IMAGES_PER_MEDIA } from "@coworker/app/src/components/RoomSettings/constants";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import { FullscreenImagePopup } from "../../../shared/components/ImageViewer/FullscreenImagePopup/FullscreenImagePopup";
import ClickableButtonWrapper from "../../../shared/wrappers/ClickableButtonWrapper/ClickableButtonWrapper";
import FixaCarousel from "../../../shared/wrappers/FixaCarousel/FixaCarousel";
import { useImageCarouselAccessibility } from "../../../shared/wrappers/FixaCarousel/useImageCarouselAccessibility";
import FixaImage from "../../../shared/wrappers/FixaImage/FixaImage";
import styles from "./media-detail-carousel.module.css";
import { useAddImagesModal } from "./useAddImagesModal";
import { useNewOrExistingFileUrls } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

interface MediaDetailCarouselProps {
  images: string[];
  title: string;
  imageAlt?: string;
}

export const MediaDetailCarousel = ({
  images,
  title,
  imageAlt,
}: MediaDetailCarouselProps) => {
  const { t } = useTranslation();
  const { carouselAccessibilityProps } = useImageCarouselAccessibility();
  const [openedImageIndex, setOpenedImageIndex] = useState<number>();
  const [confirmedImages, setConfirmedImages] = useState<{
    source: string[];
    values: string[];
  }>();
  const carouselImages =
    confirmedImages?.source.length === images.length &&
    confirmedImages.source.every((source, index) => source === images[index])
      ? confirmedImages.values
      : images;

  const {
    addImagesModalOpen,
    setAddImagesModalOpen,
    modalImages,
    setModalImages,
  } = useAddImagesModal(images);

  const modalImageUrls = useNewOrExistingFileUrls(modalImages);

  const preparedImages = useMemo(() => {
    const getImageAlt = (index: number) =>
      index === 0 && imageAlt ? imageAlt : `${title} image ${index + 1}`;

    return carouselImages.map((src, index) => ({
      src,
      alt: getImageAlt(index),
    }));
  }, [carouselImages, imageAlt, title]);

  const handleConfirmAddImages = async () => {
    setConfirmedImages({
      source: images,
      values: modalImageUrls.filter(Boolean),
    });
    setAddImagesModalOpen(false);
  };

  return (
    <div className={styles["carouselWrapper"]}>
      <FixaCarousel
        {...carouselAccessibilityProps}
        id="media-detail-carousel"
        className={styles["carousel"]}
      >
        {preparedImages.map(({ src, alt }, index) => (
          <ClickableButtonWrapper
            key={[src, index].join("-")}
            aria-label={`Image ${index + 1} button`}
            onClick={() => setOpenedImageIndex(index)}
          >
            <FixaImage
              className={styles["carouselImage"]}
              src={src}
              alt={alt}
            />
          </ClickableButtonWrapper>
        ))}
      </FixaCarousel>

      <FixaButton
        className={styles["addImagesButton"]}
        htmlType="button"
        text={t("addImagesString")}
        type="secondary"
        onClick={() => setAddImagesModalOpen(true)}
      />

      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={() => {
          setAddImagesModalOpen(false);
        }}
        onConfirmClick={handleConfirmAddImages}
        maxImages={MAX_IMAGES_PER_MEDIA}
      />

      <FullscreenImagePopup
        images={preparedImages}
        isOpen={openedImageIndex !== undefined}
        onClose={() => setOpenedImageIndex(undefined)}
        slideIndex={openedImageIndex ?? 0}
      />
    </div>
  );
};
